// src/components/reviewer/SubmittedReviews.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Eye, 
  Download,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Calendar,
  FileText,
  Star,
  Loader,
  CheckCircle,
  Mail,
  UserCheck
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { showToast } from '../../utils/toast';

// Enhanced parseQuillContent function to handle array format
const parseQuillContent = (content) => {
  if (!content) return '';

  try {
    // If it's already a string with HTML, return as is
    if (typeof content === 'string' && (content.includes('<p>') || content.includes('<div>'))) {
      return content;
    }

    // If it's an array of operations (like your email body format)
    if (Array.isArray(content)) {
      let html = '';
      content.forEach(op => {
        let text = op.insert || '';
        if (op.attributes) {
          if (op.attributes.bold) text = `<strong>${text}</strong>`;
          if (op.attributes.italic) text = `<em>${text}</em>`;
          if (op.attributes.underline) text = `<u>${text}</u>`;
          if (op.attributes.link) text = `<a href="${op.attributes.link}" target="_blank">${text}</a>`;
          if (op.attributes.color) {
            text = `<span style="color: ${op.attributes.color}">${text}</span>`;
          }
          if (op.attributes.header) {
            const level = op.attributes.header;
            text = `<h${level}>${text}</h${level}>`;
          }
          if (op.attributes.list) {
            if (op.attributes.list === 'bullet') {
              text = `<li>${text}</li>`;
            } else if (op.attributes.list === 'ordered') {
              text = `<li>${text}</li>`;
            }
          }
        }
        html += text;
      });

      // Wrap lists if needed
      if (html.includes('<li>')) {
        html = `<ul>${html}</ul>`;
      }

      // Convert newlines to <br> tags for proper formatting
      html = html.replace(/\n/g, '<br>');

      return html;
    }

    // If it's a Quill Delta object (like {"ops": [...]})
    if (typeof content === 'object' && content.ops) {
      return parseQuillContent(content.ops);
    }

    // If it's a JSON string
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        return parseQuillContent(parsed);
      } catch (e) {
        // Not JSON, return as is
        return content;
      }
    }

    return content;
  } catch (error) {
    console.error('Error parsing Quill content:', error);
    return content;
  }
};

const SubmittedReviews = () => {
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterRecommendation, setFilterRecommendation] = useState('all');
  const [filterTiming, setFilterTiming] = useState('all');
  const [filterEditorStatus, setFilterEditorStatus] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReview, setSelectedReview] = useState(null);
  const [inviteLoadingManuscriptId, setInviteLoadingManuscriptId] = useState(null);

  const navigate = useNavigate();
  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

   const location = useLocation();

   const isEditorRoute = location.pathname.includes('editors') ? true : false
    const getSubmissionsEndpoint = useCallback(() => {
      // Editor-in-Chief (or similar) sees all submissions
      if (isEditorRoute && (user?.editorialLevel === 'editor_in_chief' || user?.editorialLevel === 'editor-in-chief' || user?.editorialLevel === 'editorial_assistant')) {
        return '/editors/all-completed-reviews';
      }
      // Regular editors see only their assigned submissions
      return '/reviewer/completed-reviews';
    }, [user?.editorialLevel]);

    const endpoint = getSubmissionsEndpoint()
  // Fetch completed reviews (full dataset; search, filters, and pagination are client-side)
  useEffect(() => {
    const fetchCompletedReviews = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${endpoint}`, {
          params: {
            page: 1,
            limit: 1000
          }
        });

        if (response.data.success) {
          setReviews(response.data.reviews || []);
          setCurrentPage(1);
        }
      } catch (error) {
        console.error('Error fetching completed reviews:', error);
        showToast('Failed to load completed reviews. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchCompletedReviews();
  }, [endpoint]);

  const getRecommendationColor = (recommendation) => {
    const colors = {
      'Accept': 'bg-green-100 text-green-800 border-green-200',
      'Minor Revision': 'bg-blue-100 text-blue-800 border-blue-200',
      'Major Revision': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'Reject': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[recommendation] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const handleViewReview = (review) => {
    setSelectedReview(review);
    navigate(`/reviewerdash/review/content/${review.reviewId}/${review.manuscriptId}`)
    // setShowViewModal(true);
  };

  // Invite the responsible editor to make a decision on this manuscript
  const handleInviteEditor = async (review) => {
    setInviteLoadingManuscriptId(review.manuscriptId);
    try {
      const response = await axios.post('/reviewer/invite-editor-decision', {
        manuscriptId: review.manuscriptId
      });

      if (response.data.status === 'success') {
        showToast(response.data.message, 'success');
        // Update local state
        setReviews(prev => prev.map(r =>
          r.manuscriptId === review.manuscriptId ? { ...r, editorInvited: true } : r
        ));
      } else {
        showToast(response.data.message || 'Failed to invite the editor', 'error');
      }
    } catch (error) {
      console.error('Error inviting editor:', error);
      showToast(error.response?.data?.message || 'Failed to invite the editor. Please try again.', 'error');
    } finally {
      setInviteLoadingManuscriptId(null);
    }
  };

  // Client-side search and filters
  const filteredReviews = useMemo(() => {
    const query = (debouncedSearchTerm || '').trim().toLowerCase();

    return reviews.filter((review) => {
      if (query) {
        const searchable = [
          review.title,
          review.manuscriptId,
          review.reviewId || review.id,
          review.recommendation,
          ...(review.authors || [])
        ].filter(Boolean).join(' ').toLowerCase();
        if (!searchable.includes(query)) return false;
      }

      if (filterRecommendation !== 'all' && review.recommendation !== filterRecommendation) return false;

      if (filterTiming === 'on_time' && review.wasOnTime !== true) return false;
      if (filterTiming === 'late' && review.wasOnTime !== false) return false;

      if (filterEditorStatus === 'decided' && !review.decisionMade) return false;
      if (filterEditorStatus === 'notified' && (review.decisionMade || !review.editorInvited)) return false;
      if (filterEditorStatus === 'not_notified' && (review.decisionMade || review.editorInvited)) return false;

      return true;
    });
  }, [reviews, debouncedSearchTerm, filterRecommendation, filterTiming, filterEditorStatus]);

  // Stats computed over the filtered set
  const stats = useMemo(() => {
    const scored = filteredReviews.filter(r => r.scores?.total && !isNaN(Number(r.scores.total)));
    return {
      total: filteredReviews.length,
      onTime: filteredReviews.filter(r => r.wasOnTime === true).length,
      late: filteredReviews.filter(r => r.wasOnTime === false).length,
      avgScore: scored.length > 0
        ? Math.round(scored.reduce((acc, r) => acc + Number(r.scores.total), 0) / scored.length)
        : 0
    };
  }, [filteredReviews]);

  const itemsPerPage = 10;
  const totalItems = filteredReviews.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const displayReviews = filteredReviews.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };



  if (loading && reviews.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading submitted reviews...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-[--asfi-blue] flex items-center">
              <CheckCircle className="w-6 h-6 mr-3" />
              Reviews Submitted
            </h1>
            <p className="text-gray-600 mt-2">View all reviews you have completed and submitted</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Reviews</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-[--asfi-blue]" />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Average Score</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.avgScore}</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Star className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">On Time</p>
                  <p className="text-2xl font-bold text-green-600">{stats.onTime}</p>
                </div>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-green-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Late</p>
                  <p className="text-2xl font-bold text-red-600">{stats.late}</p>
                </div>
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-red-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by title, ID, or author..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Recommendation Filter */}
              <div>
                <select
                  value={filterRecommendation}
                  onChange={(e) => {
                    setFilterRecommendation(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">All Recommendations</option>
                  <option value="Accept">Accept</option>
                  <option value="Minor Revision">Minor Revision</option>
                  <option value="Major Revision">Major Revision</option>
                  <option value="Reject">Reject</option>
                </select>
              </div>

              {/* Timing Filter */}
              <div>
                <select
                  value={filterTiming}
                  onChange={(e) => {
                    setFilterTiming(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">All Timings</option>
                  <option value="on_time">On Time</option>
                  <option value="late">Late</option>
                </select>
              </div>

              {/* Editor Status Filter */}
              {isEditorRoute && (
                <div>
                  <select
                    value={filterEditorStatus}
                    onChange={(e) => {
                      setFilterEditorStatus(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="all">All Editor Statuses</option>
                    <option value="notified">Editor Notified</option>
                    <option value="decided">Decision Made</option>
                    <option value="not_notified">Not Notified</option>
                  </select>
                </div>
              )}
            </div>

            {/* Results Info */}
            <div className="mt-4 flex items-center justify-between text-sm">
              <p className="text-gray-600">
                Showing <span className="font-medium">{totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
                <span className="font-medium">{totalItems}</span> reviews
              </p>
              {(searchTerm || filterRecommendation !== 'all' || filterTiming !== 'all' || filterEditorStatus !== 'all') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setDebouncedSearchTerm('');
                    setFilterRecommendation('all');
                    setFilterTiming('all');
                    setFilterEditorStatus('all');
                    setCurrentPage(1);
                  }}
                  className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>

          {/* Reviews Table */}
          {reviews.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md p-12 text-center">
              <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No reviews found</h3>
              <p className="text-gray-500">You haven't submitted any reviews yet.</p>
            </div>
          ) : filteredReviews.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md p-12 text-center">
              <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No matching reviews</h3>
              <p className="text-gray-500">Try adjusting your search or filters.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-purple-600">
                    <tr>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-blue] uppercase tracking-wider">
                        Review ID
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-blue] uppercase tracking-wider">
                        Manuscript
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-blue] uppercase tracking-wider">
                        Review Date
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-blue] uppercase tracking-wider">
                        Recommendation
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-blue] uppercase tracking-wider">
                        Score
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-blue] uppercase tracking-wider">
                        Editor
                      </th>
                      <th scope="col" className="px-6 py-4 text-right text-xs font-medium text-[--asfi-blue] uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {displayReviews.map((review) => (
                      <tr key={review.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {review.reviewId || review.id}
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{review.manuscriptId}</p>
                            <p className="text-sm text-gray-500 truncate max-w-xs">{review.title}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {review.authors?.slice(0, 2).join(', ')}
                              {review.authors?.length > 2 && ' et al.'}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">{review.reviewDate}</div>
                          {review.dueDate && (
                            <div className="text-xs text-gray-400">
                              Due: {review.dueDate}
                              {review.wasOnTime ? (
                                <span className="ml-2 text-green-600">✓ On time</span>
                              ) : (
                                <span className="ml-2 text-red-600">⚠ Late</span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getRecommendationColor(review.recommendation)}`}>
                            {review.recommendation}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{review.scores.total || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {review.decisionMade ? (
                            <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border bg-blue-100 text-blue-800 border-blue-200">
                              <CheckCircle className="w-3.5 h-3.5 mr-1" />
                              Decision made
                            </span>
                          ) : review.editorInvited ? (
                            <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border bg-green-100 text-green-800 border-green-200">
                              <CheckCircle className="w-3.5 h-3.5 mr-1" />
                              Editor notified
                            </span>
                          ) : isEditorRoute ? (
                            <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border bg-gray-100 text-gray-600 border-gray-200">
                              Not notified
                            </span>
                          ) : (
                            <button
                              onClick={() => handleInviteEditor(review)}
                              disabled={inviteLoadingManuscriptId === review.manuscriptId}
                              className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Notify the responsible editor that a decision is required"
                            >
                              {inviteLoadingManuscriptId === review.manuscriptId ? (
                                <Loader className="w-3.5 h-3.5 mr-1 animate-spin" />
                              ) : (
                                <UserCheck className="w-3.5 h-3.5 mr-1" />
                              )}
                              Invite Editor
                            </button>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleViewReview(review)}
                              className="p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="View Review"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-3 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages} ({totalItems} total)
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>
                    <div className="flex gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            className={`w-8 h-8 rounded text-sm ${
                              currentPage === pageNum
                                ? 'bg-purple-600 text-white'
                                : 'border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

 
    </div>
  );
};

export default SubmittedReviews;
